import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import WorkscoreShell from '../../components/workscore/WorkscoreShell';
import ViolationWarning from '../../components/workscore/ViolationWarning';
import ProctoringAlertBanner from '../../components/assessment/ProctoringAlertBanner';
import ProctoringStatusPanel from '../../components/assessment/ProctoringStatusPanel';
import LanguageSelector from '../../components/ui/LanguageSelector';
import useWorkscoreProctoring from '../../hooks/useWorkscoreProctoring';
import { useAssessmentTranslation } from '../../hooks/useAssessmentTranslation';
import { getUIString } from '../../config/assessmentUIStrings';
import { submitWorkscoreAttempt } from '../../features/workscore/api';
import { getWorkscoreConfig } from '../../features/workscore/config';
import { useWorkscoreStore } from '../../stores/workscoreStore';
import { buildDisplayQuestion } from '../../utils/translationDisplay';

const questionVariants = {
  enter: { opacity: 0, x: 30 },
  center: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -30 }
};

function scoreForOption(question, option) {
  const row = (question?.scoreBreakdown || []).find((s) => s.option === option);
  return Number(row?.points || 0);
}

function formatTime(seconds) {
  const mm = String(Math.floor(seconds / 60)).padStart(2, '0');
  const ss = String(seconds % 60).padStart(2, '0');
  return `${mm}:${ss}`;
}

export default function WorkscoreSectionPage() {
  const navigate = useNavigate();
  const { slug, sectionNumber } = useParams();
  const numericSection = Number(sectionNumber || 1);

  const {
    attemptId,
    questionSet,
    questionsPerSection,
    currentSection,
    currentQuestionIndex,
    answers,
    violations,
    startTime,
    isActive,
    recordAnswer,
    setCurrentQuestionIndex,
    submitSection,
    setCurrentSection,
    resetAssessment
  } = useWorkscoreStore();

  const config = getWorkscoreConfig(slug);
  const assessment = questionSet;
  const [loading] = useState(false);
  const [error, setError] = useState('');
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [shake, setShake] = useState(false);
  const [sectionSeconds, setSectionSeconds] = useState(18 * 60);
  const [submitting, setSubmitting] = useState(false);
  const [latestViolation, setLatestViolation] = useState(null);

  const questionStartedAtRef = useRef(Date.now());
  const videoRef = useRef(null);
  const autoSubmittedRef = useRef(false);

  const section = assessment?.sections?.[numericSection - 1];
  const questions = section?.questions || [];
  const currentQuestion = questions[currentQuestionIndex] || null;
  const upcomingQuestions = questions.slice(currentQuestionIndex + 1, currentQuestionIndex + 3);

  const {
    currentLanguage,
    isTranslating,
    translatedContent,
    switchLanguage,
    translateCurrentQuestion
  } = useAssessmentTranslation();

  const selectedOption = useMemo(() => {
    if (!currentQuestion) return null;
    const found = answers.find((a) => a.questionId === currentQuestion.id);
    return found?.selectedOption || null;
  }, [answers, currentQuestion]);

  const sectionAnswers = useMemo(() => answers.filter((a) => a.sectionNumber === numericSection), [answers, numericSection]);

  const {
    proctoringReport,
    currentAlert,
    dismissAlert,
    totalViolations,
    tabSwitchViolations,
    faceStatus,
    gazeStatus,
    integrityScore,
    isFullscreen,
    enforceFullscreen,
    recordAnswerSignal
  } = useWorkscoreProctoring({
    slug,
    videoRef,
    enabled: Boolean(currentQuestion),
    onAutoSubmit: async () => {
      if (autoSubmittedRef.current) return;
      autoSubmittedRef.current = true;
      await handleFinalSubmit('AUTO_SUBMITTED');
    }
  });

  useEffect(() => {
    if (!isActive) {
      navigate(`/workscore/${slug}`);
      return;
    }

    if (numericSection !== currentSection) {
      navigate(`/workscore/${slug}/section/${currentSection}`, { replace: true });
      return;
    }

    if (currentSection < 1 || currentSection > 5) {
      setCurrentSection(1);
      navigate(`/workscore/${slug}/section/1`, { replace: true });
    }
  }, [currentSection, isActive, navigate, numericSection, setCurrentSection, slug]);

  useEffect(() => {
    if (!config) {
      setError('Assessment not found.');
      return;
    }
    if (!assessment?.sections?.length) {
      setError('Assessment session expired. Please restart from instructions.');
    }
  }, [assessment, config]);

  useEffect(() => {
    questionStartedAtRef.current = Date.now();
  }, [currentSection, currentQuestionIndex]);

  useEffect(() => {
    if (currentQuestion) {
      translateCurrentQuestion(currentQuestion);
    }
  }, [currentQuestion?.id, currentLanguage, translateCurrentQuestion]);

  useEffect(() => {
    if (!currentQuestion) return undefined;
    enforceFullscreen?.();
    return undefined;
  }, [currentQuestion, enforceFullscreen]);

  useEffect(() => {
    const interval = setInterval(() => {
      setSectionSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          if (!autoSubmittedRef.current) {
            autoSubmittedRef.current = true;
            handleFinalSubmit('AUTO_SUBMITTED');
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [numericSection]);

  useEffect(() => {
    window.history.pushState(null, '', window.location.href);
    const handlePopState = () => {
      window.history.pushState(null, '', window.location.href);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      localStorage.setItem('workscore-assessment-state-last-save', new Date().toISOString());
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const latest = [...violations].sort((a, b) => (a.lastOccurred < b.lastOccurred ? 1 : -1))[0];
    if (latest) setLatestViolation({ type: latest.type, count: latest.count });
  }, [violations]);

  const handleSelectOption = (option) => {
    if (!currentQuestion) return;
    const spent = Math.max(1, Math.round((Date.now() - questionStartedAtRef.current) / 1000));

    recordAnswer({
      questionId: currentQuestion.id,
      questionNumber: currentQuestion.questionNumber,
      sectionNumber: currentQuestion.sectionNumber,
      selectedOption: option,
      pointsEarned: scoreForOption(currentQuestion, option),
      timeSpentSeconds: spent,
      answeredAt: new Date().toISOString()
    });

    recordAnswerSignal?.(spent * 1000);
  };

  const nextQuestion = () => {
    if (!currentQuestion) return;
    if (!selectedOption) {
      setShake(true);
      setTimeout(() => setShake(false), 350);
      return;
    }

    if (currentQuestionIndex >= questions.length - 1) {
      setShowSubmitConfirm(true);
      return;
    }

    setCurrentQuestionIndex(currentQuestionIndex + 1);
  };

  const handleSectionSubmit = async () => {
    if (sectionAnswers.length < questions.length) {
      setShowSubmitConfirm(false);
      setShake(true);
      setTimeout(() => setShake(false), 350);
      return;
    }

    setShowSubmitConfirm(false);

    if (numericSection < (assessment?.totalSections || 5)) {
      submitSection();
      navigate(`/workscore/${slug}/section/${numericSection + 1}`);
      return;
    }

    await handleFinalSubmit('COMPLETED');
  };

  async function handleFinalSubmit(status) {
    if (!assessment || submitting) return;
    setSubmitting(true);

    try {
      const photo = sessionStorage.getItem(`${slug}_verify_photo`) || null;
      const payload = await submitWorkscoreAttempt({
        slug,
        attemptId: attemptId || sessionStorage.getItem(`workscore_attempt_${slug}`) || null,
        answers,
        violations,
        startTime: startTime || new Date().toISOString(),
        status,
        proctoringReport,
        profilePhoto: photo
      });

      sessionStorage.setItem(`workscore_result_${slug}`, JSON.stringify(payload));
      if (payload?.attemptId) {
        sessionStorage.setItem(`workscore_attempt_${slug}`, payload.attemptId);
      }
      sessionStorage.removeItem('assessment_ref');
      resetAssessment();
      navigate(`/workscore/${slug}/results`);
    } catch (err) {
      setError(err?.response?.data?.message || 'Unable to submit assessment.');
      autoSubmittedRef.current = false;
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <WorkscoreShell>
        <div className="min-h-screen flex items-center justify-center">Loading section...</div>
      </WorkscoreShell>
    );
  }

  if (error || !section || !currentQuestion) {
    return (
      <WorkscoreShell>
        <div className="min-h-screen flex items-center justify-center px-4">
          <div className="rounded-2xl border border-red-400/30 bg-red-500/10 p-6 text-center text-red-100">
            {error || 'Section data unavailable.'}
          </div>
        </div>
      </WorkscoreShell>
    );
  }

  const totalSections = assessment?.totalSections || 5;
  const progress = Math.round(((currentQuestionIndex + 1) / questions.length) * 100);
  const timerClass = sectionSeconds <= 120 ? 'text-red-400' : sectionSeconds <= 300 ? 'text-amber-300' : 'text-white';
  const displayQuestion = buildDisplayQuestion(currentQuestion, translatedContent);
  const t = (key, replacements = {}) => getUIString(currentLanguage, key, replacements);

  return (
    <WorkscoreShell>
      <video ref={videoRef} autoPlay muted playsInline style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }} aria-hidden="true" />
      <ProctoringAlertBanner message={currentAlert} onDismiss={dismissAlert} />
      <ViolationWarning
        violationType={latestViolation?.type || null}
        count={latestViolation?.count || 0}
        maxViolations={3}
      />
      <ProctoringStatusPanel
        faceStatus={faceStatus}
        gazeStatus={gazeStatus}
        violations={totalViolations}
        integrityScore={integrityScore}
        isFullscreen={isFullscreen}
        onRequestFullscreen={enforceFullscreen}
        videoRef={videoRef}
      />

      <div className="mx-auto max-w-7xl px-3 py-4 md:px-5 md:py-6">
        <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h1 className="text-lg md:text-xl font-semibold">{assessment.title}</h1>
              <p className="text-sm text-white/65">
                {t('sectionOf', { n: numericSection })} | {t('questionOf', { current: currentQuestionIndex + 1, total: questions.length })}
              </p>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <span className="rounded-full border border-white/15 bg-white/10 px-3 py-1">⚠️ {tabSwitchViolations}/3 tab violations</span>
              <span className={`font-mono text-lg ${timerClass}`}>{formatTime(sectionSeconds)}</span>
              <LanguageSelector
                currentLanguage={currentLanguage}
                onLanguageChange={(langCode) => switchLanguage(langCode, currentQuestion, upcomingQuestions)}
                isTranslating={isTranslating}
              />
            </div>
          </div>

          <div className="mt-3 h-1.5 rounded-full bg-white/10 overflow-hidden">
            <div className="h-full transition-all duration-300" style={{ width: `${progress}%`, background: config.color }} />
          </div>
        </div>

        <div className="mt-4 grid gap-4 lg:grid-cols-[220px_1fr]">
          <aside className="rounded-2xl border border-white/10 bg-white/5 p-3 h-fit">
            <p className="text-xs uppercase tracking-wide text-white/60">Section Progress</p>
            <div className="mt-3 grid grid-cols-10 gap-1.5 lg:grid-cols-5">
              {questions.map((q, idx) => {
                const answered = sectionAnswers.some((a) => a.questionId === q.id);
                return (
                  <div
                    key={q.id}
                    className={`h-7 rounded-md text-[11px] flex items-center justify-center border ${idx === currentQuestionIndex ? 'border-white bg-white/20' : answered ? 'border-emerald-300/40 bg-emerald-400/20' : 'border-white/20 bg-white/5'}`}
                  >
                    {idx + 1}
                  </div>
                );
              })}
            </div>
          </aside>

          <main className={`rounded-2xl border border-white/10 bg-white/5 p-4 md:p-6 ${shake ? 'animate-pulse' : ''}`}>
            <AnimatePresence mode="wait">
              <motion.div
                key={currentQuestion.id}
                variants={questionVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.25, ease: 'easeInOut' }}
              >
                <div className="flex flex-wrap items-center gap-2 text-xs text-white/65">
                  <span className="rounded-full border border-white/20 bg-white/10 px-2 py-1">{displayQuestion.id}</span>
                  <span className="rounded-full border border-white/20 bg-white/10 px-2 py-1">{displayQuestion.difficulty}</span>
                  <span className="rounded-full border border-white/20 bg-white/10 px-2 py-1">Question {displayQuestion.questionNumber}</span>
                  {currentLanguage !== 'en' ? (
                    <span className="inline-flex items-center gap-1 text-[10px] text-blue-400/70 bg-blue-400/10 border border-blue-400/20 rounded-full px-2 py-0.5">
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" />
                        <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10" />
                      </svg>
                      {t('translated')}
                    </span>
                  ) : null}
                </div>

                <p className={`mt-5 text-[17px] leading-[1.75] text-white/90 transition-opacity duration-200 ${isTranslating ? 'opacity-40' : 'opacity-100'}`}>
                  {displayQuestion.scenario}
                </p>

                <div className="mt-6 space-y-3">
                  {displayQuestion.options.map((option) => {
                    const selected = selectedOption === option.key;
                    return (
                      <motion.button
                        key={option.key}
                        type="button"
                        whileTap={{ scale: 0.97 }}
                        onClick={() => handleSelectOption(option.key)}
                        className={`w-full rounded-xl border px-4 py-3 text-left transition-colors ${selected ? 'text-white border-blue-400 bg-blue-500/15' : 'bg-white/5 border-white/15 hover:bg-white/10 hover:border-white/25'}`}
                        style={selected ? { borderColor: config.color } : undefined}
                      >
                        <span className="inline-flex items-center gap-3">
                          <span
                            className={`inline-flex h-7 w-7 items-center justify-center rounded-full border text-xs ${selected ? 'border-white/60 bg-white/20' : 'border-white/30 bg-white/5'}`}
                          >
                            {option.key}
                          </span>
                          <span className="text-sm md:text-base">{option.text}</span>
                        </span>
                      </motion.button>
                    );
                  })}
                </div>
              </motion.div>
            </AnimatePresence>

            {!selectedOption ? <p className="mt-3 text-xs text-amber-200">{t('selectAnOption')}</p> : null}

            <div className="mt-7 flex items-center justify-between gap-3">
              <button type="button" disabled className="rounded-xl border border-white/15 bg-white/5 px-4 py-2.5 opacity-50 cursor-not-allowed">Previous</button>
              <button
                type="button"
                disabled={submitting}
                onClick={nextQuestion}
                className="rounded-xl px-5 py-2.5 font-semibold text-white disabled:opacity-50"
                style={{ background: config.color }}
              >
                {currentQuestionIndex >= questions.length - 1 ? t('submitSection') : t('nextQuestion')}
              </button>
            </div>
          </main>
        </div>
      </div>

      {showSubmitConfirm ? (
        <div className="fixed inset-0 z-[1400] bg-black/55 backdrop-blur-sm flex items-center justify-center px-4">
          <div className="w-full max-w-lg rounded-2xl border border-white/15 bg-[#141414] p-6">
            <h2 className="text-xl font-semibold">Complete Section {numericSection}</h2>
            <p className="mt-3 text-sm text-white/75">
              You are about to complete Section {numericSection}: {section.title}. You have answered all {questions.length} questions for this section. Once submitted, you cannot return to this section.
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button type="button" onClick={() => setShowSubmitConfirm(false)} className="rounded-lg border border-white/20 bg-white/10 px-4 py-2">{t('cancel')}</button>
              <button type="button" onClick={handleSectionSubmit} className="rounded-lg px-4 py-2 font-semibold text-white" style={{ background: config.color }}>
                {t('confirmSubmit')}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </WorkscoreShell>
  );
}
